import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, X, Loader2 } from "lucide-react";
import { useAccess } from "@/contexts/AccessContext";

export default function EmailCapture({ onClose, onSubmit }) {
  const { setUserEmail } = useAccess();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Save email to context and localStorage
      setUserEmail(email.trim().toLowerCase());
      
      // Mark that user has provided email
      localStorage.setItem("sib_email_captured", "true");
      
      toast.success("You're on the list! We'll send you helpful tips.");
      onSubmit?.(email);
      onClose();
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    // Mark that user has seen the prompt (don't show again this session)
    localStorage.setItem("sib_email_prompt_seen", "true");
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm p-4"
      data-testid="email-capture"
    >
      <div className="bg-white rounded-2xl p-6 max-w-md w-full animate-slideUp shadow-xl">
        {/* Close button */}
        <div className="flex justify-end mb-2">
          <button 
            onClick={handleSkip}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="close-email-capture"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        {/* Icon */}
        <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-[#EAF6F4] flex items-center justify-center">
          <Mail className="text-[#4FB3A5]" size={24} />
        </div>

        {/* Content */}
        <h3 className="text-xl font-bold text-[#1F3A5F] text-center mb-2">
          Keep improving your messages
        </h3>
        <p className="text-[#6B7280] text-center mb-6 leading-relaxed text-sm">
          Enter your email to get communication tips, updates, and priority access to new features.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-xl border-gray-200 focus:border-[#4FB3A5] focus:ring-[#4FB3A5]/20 py-5"
            data-testid="email-input"
          />
          
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-xl bg-[#4FB3A5] hover:bg-[#3d9a8d] py-5 font-semibold"
            data-testid="email-submit-button"
          >
            {isSubmitting ? (
              <Loader2 className="animate-spin" size={18} />
            ) : (
              "Send me tips"
            )}
          </Button>
          
          <button
            type="button"
            onClick={handleSkip}
            className="w-full text-center text-sm text-[#6B7280] hover:text-[#4FB3A5] transition-colors py-2"
            data-testid="email-skip-button"
          >
            Not now
          </button>
        </form>
      </div>
    </div>
  );
}
